import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { eventsApi, paymentApi, bodiesOfWaterApi } from '../utils/api';
import { IconBoat, IconFish, IconCheckCircle } from '../components/Icons';

export default function CreateEvent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '', description: '', location: '',
    start_date: '', end_date: '',
    max_attendees: 50,
    event_type: 'team',
    waiver_text: '', terms_and_conditions: '',
    branding_primary_color: '#1a3c5e',
    branding_secondary_color: '#e8a838'
  });
  const [createdEvent, setCreatedEvent] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [squareAppId, setSquareAppId] = useState(null);
  const [squareLocationId, setSquareLocationId] = useState(null);
  const [sandboxBypass, setSandboxBypass] = useState(false);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const cardInstanceRef = useRef(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [bodiesOfWater, setBodiesOfWater] = useState([]);

  useEffect(() => {
    paymentApi.getSquareAppId().then(r => {
      setSquareAppId(r.data.data.app_id);
      setSquareLocationId(r.data.data.location_id);
      if (r.data.data.sandbox_bypass) setSandboxBypass(true);
    }).catch(() => {});
    // Fetch live pricing tiers so the step-1 estimate always reflects current admin settings
    eventsApi.getPricingTiers()
      .then(r => { if (r.data.success) setPricingTiers(r.data.data); })
      .catch(() => {});
    // Fetch bodies of water for region dropdown
    bodiesOfWaterApi.listPublic()
      .then(r => { if (r.data.success) setBodiesOfWater(r.data.data); })
      .catch(() => {});
  }, []);

  const pricingEstimate = getPricingEstimate(pricingTiers, form.max_attendees);

  async function handleStep1(e) {
    e.preventDefault();
    setError('');
    if (!form.name || !form.start_date || !form.end_date) {
      return setError('Name, start date, and end date are required');
    }
    if (new Date(form.end_date) <= new Date(form.start_date)) {
      return setError('End date must be after start date');
    }
    setStep(2);
  }

  async function handleApplyCoupon() {
    setCouponError('');
    if (!couponCode.trim()) return setCouponError('Enter a coupon code');
    setCouponLoading(true);
    try {
      const { data } = await eventsApi.validateCoupon({ code: couponCode.trim() });
      setAppliedCoupon(data.data);
      setCouponError('');
    } catch (err) {
      setCouponError(err.response?.data?.message || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  }

  function handleImageSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
    if (!allowed.includes(file.type)) {
      setError('Image must be JPG, PNG, WebP, or AVIF');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setError('Image must be under 4MB');
      return;
    }
    setError('');
    setFeaturedImage(file);
    setFeaturedImagePreview(URL.createObjectURL(file));
  }

  async function handleCreateEvent() {
    setError(''); setLoading(true);
    try {
      const payload = { ...form };
      // Convert datetime-local strings to ISO with timezone
      if (payload.start_date) payload.start_date = new Date(payload.start_date).toISOString();
      if (payload.end_date) payload.end_date = new Date(payload.end_date).toISOString();
      if (appliedCoupon) payload.coupon_code = appliedCoupon.code;
      const { data } = await eventsApi.createEvent(payload);
      const created = data.data;

      // Upload featured image if selected
      if (featuredImage) {
        try {
          const fd = new FormData();
          fd.append('featured_image', featuredImage);
          await eventsApi.uploadFeaturedImage(created.id, fd);
        } catch (imgErr) {
          console.error('Featured image upload failed:', imgErr);
        }
      }

      setCreatedEvent(created);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  }

  const initSquareCard = useCallback(async () => {
    if (!window.Square || !squareAppId || !squareLocationId) return;
    try {
      const payments = window.Square.payments(squareAppId, squareLocationId);
      const card = await payments.card();
      await card.attach('#card-container');
      cardInstanceRef.current = card;
      setCardReady(true);
    } catch (err) {
      console.error('Square card init error:', err);
      setError('Failed to load payment form. Please refresh and try again.');
    }
  }, [squareAppId, squareLocationId]);

  const isFreeEvent = createdEvent && parseFloat(createdEvent.amount_due) <= 0;

  useEffect(() => {
    if (step === 3 && createdEvent && squareAppId && squareLocationId && !isFreeEvent) {
      // Small delay to ensure the DOM element is rendered
      const timer = setTimeout(initSquareCard, 100);
      return () => {
        clearTimeout(timer);
        if (cardInstanceRef.current) {
          cardInstanceRef.current.destroy();
          cardInstanceRef.current = null;
          setCardReady(false);
        }
      };
    }
  }, [step, createdEvent, squareAppId, squareLocationId, isFreeEvent, initSquareCard]);

  async function handleFreeActivate() {
    setError('');
    setPaymentProcessing(true);
    try {
      const { data } = await paymentApi.payForEvent(createdEvent.id, 'coupon-free');
      if (data.success) {
        setStep(4);
      } else {
        setError(data.message || 'Activation failed.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Activation failed.');
    } finally {
      setPaymentProcessing(false);
    }
  }

  async function handleSandboxActivate() {
    setError('');
    setPaymentProcessing(true);
    try {
      const { data } = await paymentApi.payForEvent(createdEvent.id, 'sandbox-bypass');
      if (data.success) {
        setStep(4);
      } else {
        setError(data.message || 'Activation failed.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Activation failed.');
    } finally {
      setPaymentProcessing(false);
    }
  }

  async function handlePayment() {
    setError('');
    if (!cardInstanceRef.current) {
      return setError('Payment form not ready. Please wait a moment and try again.');
    }
    setPaymentProcessing(true);
    try {
      const result = await cardInstanceRef.current.tokenize();
      if (result.status !== 'OK') {
        setPaymentProcessing(false);
        return setError(result.errors?.[0]?.message || 'Card verification failed. Please check your card details.');
      }
      const { data } = await paymentApi.payForEvent(createdEvent.id, result.token);
      if (data.success) {
        setStep(4);
      } else {
        setError(data.message || 'Payment failed. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Payment processing failed. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  }

  const EventTypeCard = ({ value, icon, title, desc }) => {
    const selected = form.event_type === value;
    return (
      <button
        type="button"
        onClick={() => setForm(f => ({ ...f, event_type: value }))}
        style={{
          flex: 1, padding: '20px 16px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
          background: selected ? 'rgba(232,168,56,0.12)' : '#111f2e',
          border: `2px solid ${selected ? '#e8a838' : '#2a3f56'}`,
          transition: 'all 0.2s'
        }}
      >
        <div style={{ marginBottom: 8, color: selected ? '#e8a838' : '#8a9bb5' }}>{icon}</div>
        <div style={{ color: selected ? '#e8a838' : '#f0f2f5', fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>{title}</div>
        <div style={{ color: '#8a9bb5', fontSize: '0.75rem', lineHeight: 1.5 }}>{desc}</div>
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${selected ? '#e8a838' : '#2a3f56'}`, background: selected ? '#e8a838' : 'transparent', transition: 'all 0.2s', flexShrink: 0 }} />
          <span style={{ color: selected ? '#e8a838' : '#5c6d83', fontSize: '0.72rem', fontWeight: selected ? 600 : 400 }}>
            {selected ? 'Selected' : 'Select'}
          </span>
        </div>
      </button>
    );
  };

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header">
          <Link to="/dashboard" style={{ fontSize: '0.82rem', color: '#8a9bb5', display: 'inline-block', marginBottom: 12 }}>← Back to Dashboard</Link>
          <h1>Create New Event</h1>
          <p>Set up your fishing derby in a few steps</p>
        </div>

        {/* Progress steps */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 32 }}>
          {['Details', 'Rules & Branding', 'Payment', 'Ready'].map((label, i) => {
            const s = i + 1;
            const isActive = step === s;
            const isDone = step > s;
            return (
              <div key={s} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: isDone ? '#3adc8a' : isActive ? '#e8a838' : '#2a3f56',
                    color: isDone || isActive ? '#0d1b2a' : '#8a9bb5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.75rem', fontWeight: 700, flexShrink: 0
                  }}>
                    {isDone ? '✓' : s}
                  </div>
                  <span style={{ fontSize: '0.78rem', color: isActive ? '#f0f2f5' : '#8a9bb5', fontWeight: isActive ? 600 : 400, whiteSpace: 'nowrap' }}>{label}</span>
                </div>
                {i < 3 && <div style={{ flex: 1, height: 2, background: step > s ? '#3adc8a' : '#2a3f56', margin: '0 8px' }} />}
              </div>
            );
          })}
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* ── STEP 1: Event Details ── */}
        {step === 1 && (
          <div className="card">
            <h3 style={{ marginBottom: 20 }}>Event Details</h3>
            <form onSubmit={handleStep1}>

              {/* Event type selector */}
              <div className="form-group">
                <label>Event Format</label>
                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                  <EventTypeCard
                    value="team"
                    icon={<IconBoat size="22px" />}
                    title="Team Event"
                    desc="Teams of anglers compete together. Supports team captains, roster management, and team-based scoring."
                  />
                  <EventTypeCard
                    value="individual"
                    icon={<IconFish size="22px" />}
                    title="Individual Event"
                    desc="Solo anglers compete independently. Each participant has their own catch log and score."
                  />
                </div>
                <div style={{ marginTop: 8, fontSize: '0.72rem', color: '#5c6d83' }}>
                  {form.event_type === 'team'
                    ? '→ Catch Logs will be logged per team. Teams tab will be the primary view.'
                    : '→ Catch Logs will be logged per participant. Participants tab will be the primary view.'}
                </div>
              </div>

              <div className="form-group">
                <label>Event Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Lake Ontario Bass Classic 2025" required />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Tell anglers about your derby... (HTML supported for links)" />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Kawartha Lakes, Ontario" />
              </div>
              <div className="form-group">
                <label>Body of Water *</label>
                {bodiesOfWater.length > 0 ? (
                  <select
                    value={form.body_of_water_id || ''}
                    onChange={e => setForm({ ...form, body_of_water_id: e.target.value || null })}
                    required
                  >
                    <option value="">— Select a body of water —</option>
                    {bodiesOfWater.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                ) : (
                  <div style={{ padding: '10px 14px', background: '#1e2d3d', borderRadius: 8, color: '#8a9bb5', fontSize: '0.85rem' }}>
                    No bodies of water configured. Contact the platform administrator.
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="datetime-local" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="datetime-local" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label>Expected Attendees</label>
                <input type="number" value={form.max_attendees} onChange={e => setForm({ ...form, max_attendees: e.target.value === '' ? '' : parseInt(e.target.value) })} onBlur={() => { if (!form.max_attendees) setForm(f => ({ ...f, max_attendees: 50 })); }} min={1} />
                <div style={{ marginTop: 8, padding: '10px 14px', background: '#1e2d3d', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8a9bb5', fontSize: '0.8rem' }}>Estimated Tier</span>
                  <span style={{ color: '#e8a838', fontSize: '0.8rem', fontWeight: 600 }}>
                    {pricingEstimate ? `${pricingEstimate.tier} — $${pricingEstimate.price} CAD` : 'Exceeds max tier'}
                  </span>
                </div>
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}>Continue →</button>
            </form>
          </div>
        )}

        {/* ── STEP 2: Rules & Branding ── */}
        {step === 2 && (
          <div className="card">
            <h3 style={{ marginBottom: 4 }}>Rules, Waivers & Branding</h3>
            <p style={{ color: '#8a9bb5', fontSize: '0.82rem', marginBottom: 20 }}>
              You can add tournament-specific rules after the event is created.
            </p>
            <div className="form-group">
              <label>Event Waiver <span style={{ color: '#5c6d83', fontWeight: 400 }}>(optional)</span></label>
              <textarea value={form.waiver_text} onChange={e => setForm({ ...form, waiver_text: e.target.value })} placeholder="Enter your event waiver text..." />
            </div>
            <div className="form-group">
              <label>Terms & Conditions <span style={{ color: '#5c6d83', fontWeight: 400 }}>(optional)</span></label>
              <textarea value={form.terms_and_conditions} onChange={e => setForm({ ...form, terms_and_conditions: e.target.value })} placeholder="Event terms and conditions..." />
            </div>
            <div className="form-group">
              <label>Featured Image <span style={{ color: '#5c6d83', fontWeight: 400 }}>(optional)</span></label>
              <p style={{ color: '#5c6d83', fontSize: '0.75rem', margin: '0 0 8px' }}>JPG, PNG, WebP, or AVIF — max 4MB</p>
              {featuredImagePreview ? (
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <img src={featuredImagePreview} alt="Preview" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 10, border: '1px solid #2a3f56' }} />
                  <button type="button" onClick={() => { setFeaturedImage(null); setFeaturedImagePreview(null); }} style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.7)', color: '#e85d5d', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.8rem' }}>Remove</button>
                </div>
              ) : (
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '24px 16px', border: '2px dashed #2a3f56', borderRadius: 10, cursor: 'pointer', color: '#8a9bb5', fontSize: '0.85rem' }}>
                  <span>Click to select image</span>
                  <input type="file" accept=".jpg,.jpeg,.png,.webp,.avif" onChange={handleImageSelect} style={{ display: 'none' }} />
                </label>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label>Primary Color</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="color" value={form.branding_primary_color} onChange={e => setForm({ ...form, branding_primary_color: e.target.value })} style={{ width: 44, height: 44, padding: 2, cursor: 'pointer', borderRadius: 6, border: '1px solid #2a3f56', background: 'transparent' }} />
                  <input value={form.branding_primary_color} onChange={e => setForm({ ...form, branding_primary_color: e.target.value })} style={{ flex: 1, fontFamily: 'monospace' }} />
                </div>
              </div>
              <div className="form-group">
                <label>Accent Color</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="color" value={form.branding_secondary_color} onChange={e => setForm({ ...form, branding_secondary_color: e.target.value })} style={{ width: 44, height: 44, padding: 2, cursor: 'pointer', borderRadius: 6, border: '1px solid #2a3f56', background: 'transparent' }} />
                  <input value={form.branding_secondary_color} onChange={e => setForm({ ...form, branding_secondary_color: e.target.value })} style={{ flex: 1, fontFamily: 'monospace' }} />
                </div>
              </div>
            </div>

            {/* Summary of event_type */}
            <div style={{ background: '#111f2e', border: '1px solid #2a3f56', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: '#8a9bb5' }}>{form.event_type === 'team' ? <IconBoat size="20px" /> : <IconFish size="20px" />}</span>
              <div>
                <div style={{ color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem' }}>
                  {form.event_type === 'team' ? 'Team Event' : 'Individual Event'}
                </div>
                <div style={{ color: '#8a9bb5', fontSize: '0.72rem' }}>
                  {form.event_type === 'team' ? 'Teams will be registered and scored together' : 'Individual participants will be scored separately'}
                </div>
              </div>
              <button type="button" onClick={() => setStep(1)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#4a9eff', fontSize: '0.75rem', cursor: 'pointer' }}>
                Change ↗
              </button>
            </div>

            {/* Coupon Code */}
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Coupon Code <span style={{ color: '#5c6d83', fontWeight: 400 }}>(optional)</span></label>
              {appliedCoupon ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'rgba(58,220,138,0.1)', border: '1px solid #3adc8a', borderRadius: 8 }}>
                  <span style={{ color: '#3adc8a', fontWeight: 600 }}>{appliedCoupon.code}</span>
                  <span style={{ color: '#3adc8a', fontSize: '0.85rem' }}>— {appliedCoupon.discount_percent}% off</span>
                  <button type="button" onClick={() => { setAppliedCoupon(null); setCouponCode(''); setCouponError(''); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#e85d5d', fontSize: '0.8rem', cursor: 'pointer' }}>Remove</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleApplyCoupon} disabled={couponLoading}>
                    {couponLoading ? '...' : 'Apply'}
                  </button>
                </div>
              )}
              {couponError && <div style={{ color: '#e85d5d', fontSize: '0.8rem', marginTop: 6 }}>{couponError}</div>}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-secondary" onClick={() => setStep(1)}>← Back</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleCreateEvent} disabled={loading}>
                {loading ? 'Creating...' : 'Create Event & Proceed to Payment →'}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Payment ── */}
        {step === 3 && createdEvent && (
          <div className="card">
            <h3 style={{ marginBottom: 8 }}>Payment Required</h3>
            <p style={{ color: '#8a9bb5', fontSize: '0.85rem', marginBottom: 24 }}>Your event has been created. Complete payment to activate it.</p>
            <div style={{ background: '#1e2d3d', borderRadius: 10, padding: 20, marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ color: '#8a9bb5' }}>Event</span>
                <span style={{ color: '#f0f2f5', fontWeight: 500 }}>{createdEvent.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ color: '#8a9bb5' }}>Format</span>
                <span style={{ color: '#f0f2f5', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {createdEvent.event_type === 'team' ? <><IconBoat size="14px" />Team</> : <><IconFish size="14px" />Individual</>}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ color: '#8a9bb5' }}>Tier</span>
                <span style={{ color: '#f0f2f5' }}>{createdEvent.tier?.tier_name} (up to {createdEvent.tier?.max_attendees} attendees)</span>
              </div>
              {parseFloat(createdEvent.discount_amount) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ color: '#3adc8a' }}>Coupon Discount {createdEvent.coupon_code && <span style={{ fontWeight: 600 }}>({createdEvent.coupon_code})</span>}</span>
                  <span style={{ color: '#3adc8a', fontWeight: 600 }}>-${parseFloat(createdEvent.discount_amount).toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid #2a3f56' }}>
                <span style={{ color: '#f0f2f5', fontWeight: 600 }}>Total Due</span>
                <span style={{ color: '#e8a838', fontFamily: 'Oswald, sans-serif', fontSize: '1.4rem', fontWeight: 700 }}>
                  ${createdEvent.amount_due} <span style={{ fontSize: '0.7rem', color: '#8a9bb5' }}>CAD</span>
                </span>
              </div>
            </div>
            {isFreeEvent ? (
              <div style={{ marginBottom: 20, padding: 16, background: 'rgba(58,220,138,0.08)', borderRadius: 8, border: '1px solid #3adc8a' }}>
                <p style={{ color: '#3adc8a', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>No Payment Required</p>
                <p style={{ color: '#8a9bb5', fontSize: '0.78rem' }}>Your coupon covers the full amount. Click below to activate your event.</p>
              </div>
            ) : sandboxBypass ? (
              <div style={{ marginBottom: 20, padding: 16, background: '#1a2e1a', borderRadius: 8, border: '1px solid #2a5a2a' }}>
                <p style={{ color: '#3adc8a', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>Sandbox Mode</p>
                <p style={{ color: '#8a9bb5', fontSize: '0.78rem' }}>Square credentials not configured. Payment will be bypassed and the event will be activated directly.</p>
              </div>
            ) : (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem', marginBottom: 10 }}>Card Details</label>
                <div id="card-container" style={{ minHeight: 50, background: '#111f2e', borderRadius: 8, padding: '12px', border: '1px solid #2a3f56' }}></div>
                {!cardReady && squareAppId && (
                  <p style={{ color: '#8a9bb5', fontSize: '0.78rem', marginTop: 8 }}>Loading payment form...</p>
                )}
                {!squareAppId && (
                  <p style={{ color: '#e85d5d', fontSize: '0.78rem', marginTop: 8 }}>Payment configuration not available. Please contact support.</p>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-secondary" onClick={() => setStep(2)} disabled={paymentProcessing}>← Back</button>
              {isFreeEvent ? (
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleFreeActivate} disabled={paymentProcessing}>
                  {paymentProcessing ? 'Activating...' : 'Activate Event (Free) →'}
                </button>
              ) : sandboxBypass ? (
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSandboxActivate} disabled={paymentProcessing}>
                  {paymentProcessing ? 'Activating...' : `Activate Event (Sandbox) →`}
                </button>
              ) : (
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={handlePayment} disabled={paymentProcessing || !cardReady}>
                  {paymentProcessing ? 'Processing Payment...' : `Pay $${createdEvent.amount_due} CAD →`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── STEP 4: Success ── */}
        {step === 4 && createdEvent && (
          <div className="card" style={{ textAlign: 'center', padding: '48px 32px' }}>
            <div style={{ marginBottom: 16, color: '#3adc8a' }}><IconCheckCircle size="56px" /></div>
            <h2 style={{ color: '#3adc8a', marginBottom: 8 }}>Payment Successful!</h2>
            <p style={{ color: '#8a9bb5', marginBottom: 24 }}>
              <strong style={{ color: '#f0f2f5' }}>{createdEvent.name}</strong> is now active.
              {createdEvent.event_type === 'team'
                ? ' Add tournaments, teams, and sponsors from the event page.'
                : ' Add tournaments, participants, and sponsors from the event page.'}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to={`/events/${createdEvent.id}`} className="btn btn-primary">Manage Event →</Link>
              <Link to={`/events/${createdEvent.id}/scoreboard`} className="btn btn-secondary" target="_blank">Preview Scoreboard</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function getPricingEstimate(tiers, attendees) {
  if (!tiers || tiers.length === 0) return null;
  const match = tiers.find(t => attendees >= t.min_attendees && attendees <= t.max_attendees);
  if (!match) return null;
  return { tier: match.tier_name, price: parseFloat(match.price_cad).toFixed(2) };
}
